import { Application } from '../../config'
import { BACKWARD_DIRECTION, compareStops, FORWARD_DIRECTION, hasInflexion, isWheelPicker, singleStopPick, stopInDirection, tripDirection } from '../../helpers'
import { IAvailability, IFavoriteResponse, IPaymentActions, IPaymentState, IReservation, IServiceInfo, IStop } from '../../models'
import { LiteralsService } from '../../services'
import { INewReservationState } from '../states'
import {
  BusStopsStepTypeKeys,
  ConfirmStepTypeKeys,
  DateStepTypeKeys,
  NewReservationActionTypes,
  RouteOptionStepTypeKeys
} from '../types'

export const initialNewReservationState: INewReservationState = {
  isEdit: false,
  pickedReservationId: undefined,
  serviceInfos: [],
  isFetchingServices: false,
  isSearching: false,
  nameStopSearched: '',
  stopsSearched: [],
  searchedType: undefined,
  coordinates: undefined,
  pickedService: undefined,
  pickedOriginBusStop: undefined,
  isFetchingDestinationBusStops: false,
  destinationBusStops: [],
  pickedDestinationBusStop: undefined,
  specificDate: false,
  pickedDate: new Date(),
  pickedSeats: 1,
  pickedPrmSeats: 0,
  isFetchingRouteOptions: false,
  routeOptions: [],
  pickedAvailability: undefined,
  isConfirmingReservation: false,
  isMarkerSelected: false,
  mapChangedCenter: null,
  paymentState: IPaymentState.PAYMENT_NOT_DO_IT,
  paymentAction: IPaymentActions.BOOK,
  isWheelPicker: true,
  isRoundTrip: false,
  returnPickedDate: new Date(),
  returnSpecificDate: false,
  weekDaysListReturn: [],
  showTripTypeOptions: false,
  isSingleStopModalOpen: false,
  isOrigin: true,
  isSearchingCoordsInMap: false,
  isMapCenterWithinOutlines: true,
  previewOriginStop: undefined,
  previewDestinationStop: undefined,
  cachedAddresses: [],
  stopAvailabilityTimes: [],
  isFetchingStopAvailabilityTimes: false
}

const { isAmb } = Application;

const CACHED_ADDRESSES_LIMIT = 50

function updateBusStops(
  state: INewReservationState,
  stop: IStop
): INewReservationState {
  if (!state.pickedOriginBusStop && !state.pickedDestinationBusStop) {
    return {
      ...state,
      pickedOriginBusStop: stop,
      coordinates: stop.location
    }
  } else if (
    state.pickedOriginBusStop &&
    !compareStops(state.pickedOriginBusStop, stop) &&
    !state.pickedDestinationBusStop
  ) {
    const { isRoundTrip, showTripTypeOptions } = checkIfIsRoundTripAndShowTripTypeOptions(state.serviceInfos, state.pickedOriginBusStop, stop)
    return {
      ...state,
      pickedDestinationBusStop: stop,
      pickedServiceInfo: stop.serviceInfo?.id,
      showTripTypeOptions,
      isRoundTrip
    }
  } else if (
    !!state.pickedDestinationBusStop &&
    compareStops(state.pickedDestinationBusStop, stop)
  ) {
    return {
      ...state,
      pickedDestinationBusStop: initialNewReservationState.pickedDestinationBusStop,
      showTripTypeOptions: false,
      singleStopSelected: undefined
    }
  }

  return {
    ...state,
    routeOptionsAndFailures: undefined,
    pickedService: initialNewReservationState.pickedService,
    pickedOriginBusStop: initialNewReservationState.pickedOriginBusStop,
    pickedDestinationBusStop: initialNewReservationState.pickedDestinationBusStop,
    pickedAvailability: undefined,
    pickedDate: new Date(),
    returnPickedAvailability: undefined,
    returnRouteOptionsAndFailures: undefined,
    returnPickedDate: new Date(),
    destinationBusStops: initialNewReservationState.destinationBusStops,
    pickedServiceInfo: undefined,
    showTripTypeOptions: false,
    singleStopSelected: undefined
  }
}

/**
 * What an edit starts from. A round trip is edited as one: whichever of the two was opened, the
 * edit always starts from the outbound trip (forward stops) and the return is its reverse, exactly
 * as when booking. A single stop trip keeps only the stop the user picked, as a new booking does.
 * The service's stops tell the direction and the line ends, so this is worked out again once the
 * service is loaded.
 */
function editTrip(reservation: IReservation, serviceInfo?: IServiceInfo): Partial<INewReservationState> {
  const hasRelated = !!reservation.relatedReservationId
  const beginningStop = reservation.beginningStop
  const exitStop = reservation.seats[0].exitStop

  // Without directions, the earlier trip of the pair is the outbound one.
  const direction = tripDirection(beginningStop, exitStop, serviceInfo)
  const isReturnTrip = direction !== undefined
    ? direction === BACKWARD_DIRECTION
    : hasRelated && !!reservation.returnDate &&
      new Date(reservation.returnDate).getTime() < new Date(reservation.dateTime).getTime()
  const startsFromRelated = hasRelated && isReturnTrip

  const tripBeginning = startsFromRelated ? stopInDirection(exitStop, FORWARD_DIRECTION, serviceInfo) : beginningStop
  const tripExit = startsFromRelated ? stopInDirection(beginningStop, FORWARD_DIRECTION, serviceInfo) : exitStop

  // The service's own stops, so the list and the map recognise them.
  const asServiceStop = (stop: IStop): IStop => serviceInfo
    ? { ...(serviceInfo.stops?.find((serviceStop) => serviceStop.id === stop.id) ?? stop), serviceInfo }
    : stop

  let stops: Partial<INewReservationState> = {
    pickedOriginBusStop: asServiceStop(tripBeginning),
    pickedDestinationBusStop: asServiceStop(tripExit),
    singleStopSelected: undefined,
    isOrigin: true
  }
  if (serviceInfo?.singleStopReservations) {
    const { stop, isOrigin } = singleStopPick(tripBeginning, tripExit, serviceInfo)
    const userStop = asServiceStop(stop)
    stops = {
      pickedOriginBusStop: isOrigin ? userStop : undefined,
      pickedDestinationBusStop: isOrigin ? undefined : userStop,
      singleStopSelected: userStop,
      isOrigin
    }
  }

  const availability = (id?: number) => id ? { id } as IAvailability : undefined

  return {
    ...stops,
    pickedReservationId: startsFromRelated ? reservation.relatedReservationId : reservation.id,
    pickedRelatedReservationId: startsFromRelated ? reservation.id : reservation.relatedReservationId,
    pickedDate: startsFromRelated ? reservation.returnDate : reservation.dateTime,
    returnPickedDate: startsFromRelated ? reservation.dateTime : reservation.returnDate,
    pickedAvailability: availability(startsFromRelated ? reservation.returnAvailabilityId : reservation.availabilityId),
    returnPickedAvailability: availability(startsFromRelated ? reservation.availabilityId : reservation.returnAvailabilityId)
  }
}

/**
 * A single stop trip with no inflexion on its line is no round trip, and which of its two stops
 * the user picked cannot be told. As an exception, it is edited as an origin to destination trip:
 * both stops picked, and the service read as a regular one for as long as the edit lasts.
 */
function editableServiceInfo(serviceInfo: IServiceInfo): IServiceInfo {
  const isKnown = !!serviceInfo.stops?.length
  if (!serviceInfo.singleStopReservations || !isKnown || hasInflexion(serviceInfo)) return serviceInfo
  return { ...serviceInfo, singleStopReservations: false }
}

function reservationToReservationState(
  state: INewReservationState,
  reservation: IReservation
): INewReservationState {
  const serviceInfo = editableServiceInfo(state.serviceInfos.find((serviceInfo) => serviceInfo.id === reservation.serviceInfo.id) ?? reservation.serviceInfo)

  return {
    ...initialNewReservationState,
    serviceInfos: state.serviceInfos,
    isEdit: true,
    editingReservation: reservation,
    pickedService: reservation.service.id,
    pickedServiceInfo: reservation.serviceInfo.id,
    pickedSeats: reservation.seats.filter(seat => seat.prm === false).length,
    pickedPrmSeats: reservation.seats.filter(seat => seat.prm).length,
    pickedTown: reservation.townId,
    isRoundTrip: !!reservation.relatedReservationId,
    ...editTrip(reservation, serviceInfo)
  }
}

function favoriteToReservationState(
  state: INewReservationState,
  favorite: IFavoriteResponse
): INewReservationState {
  const pickedServiceInfo = state.serviceInfos.find(serviceInfo => serviceInfo.id === favorite.serviceInfoId)
  return {
    ...initialNewReservationState,
    serviceInfos: state.serviceInfos,
    pickedService: favorite.service.id,
    pickedOriginBusStop: {
      ...favorite.inStop.properties,
      location: favorite.inStop,
      serviceInfo: pickedServiceInfo
    } as any,
    pickedDestinationBusStop: {
      ...favorite.exitStop.properties,
      location: favorite.exitStop,
      serviceInfo: pickedServiceInfo
    } as any,
    isWheelPicker: state.isWheelPicker
  }
}

function updateServices(
  state: INewReservationState,
  serviceInfos: IServiceInfo[]
): INewReservationState {
  const { isRoundTrip, showTripTypeOptions } = checkIfIsRoundTripAndShowTripTypeOptions(serviceInfos, state.pickedOriginBusStop, state.pickedDestinationBusStop)
  const updatedServiceInfos = serviceInfos.map((s) => {
    const serviceInfo = {
      ...s,
      selected: s.selected !== undefined ? s.selected : true,
      stops: s.stops.map((st) => {
        if (st.id > 0) return {...st, name: `${st.name} (${LiteralsService.get('finalStopTag')})`}
        return st
      })}
    return state.isEdit && serviceInfo.id === state.pickedServiceInfo ? editableServiceInfo(serviceInfo) : serviceInfo
  })
  // An edit works its trip out again now that the service's stops are known, once: from then on
  // the stops are the user's to change.
  const pickedServiceInfo = state.isEdit && state.editingReservation
    ? updatedServiceInfos.find((serviceInfo) => serviceInfo.id === state.pickedServiceInfo)
    : undefined
  const editedTrip = pickedServiceInfo
    ? { ...editTrip(state.editingReservation!, pickedServiceInfo), editingReservation: undefined }
    : {}

  return {
    ...state,
    serviceInfos: updatedServiceInfos,
    isWheelPicker: isWheelPicker(serviceInfos),
    isRoundTrip: isRoundTrip || state.isRoundTrip,
    showTripTypeOptions,
    ...editedTrip
  }
}

function checkIfIsRoundTripAndShowTripTypeOptions(serviceInfos: IServiceInfo[], origin?: IStop, destination?: IStop) {
  const hasRoundTripServiceInfos = serviceInfos.some((service) => service.roundTrips);
  if (origin && destination && !isAmb) {
    const originServices = serviceInfos.filter((service) => service.stops.some((stop) => stop.id === origin.id))
    const destinationServices = serviceInfos.filter((service) => service.stops.some((stop) => stop.id === destination.id))
    const commonServices = originServices.filter((originService) => destinationServices.includes(originService))
    if (commonServices.length === 1) {
      return { isRoundTrip: commonServices[0].roundTrips, showTripTypeOptions: hasRoundTripServiceInfos ? false : true }
    } else if (commonServices.length > 0) {
      return { isRoundTrip: true, showTripTypeOptions: true }
    } else {
      return { isRoundTrip: false, showTripTypeOptions: hasRoundTripServiceInfos ? false : true }
    }
  } else if ((origin && !destination) || (!origin && destination)) {  // single stop
    const singleStop = origin! || destination!
    return { isRoundTrip: singleStop.serviceInfo.roundTrips, showTripTypeOptions: true }
  } else {
    return { isRoundTrip: hasRoundTripServiceInfos, showTripTypeOptions: false }
  }
}

function getSelected(s: IServiceInfo, serviceInfo: IServiceInfo, selected: boolean): boolean {
  if (s.id === serviceInfo.id) return selected
  else return s.selected
}

function setServices(
  state: INewReservationState,
  payload: any
): INewReservationState {
  return {
    ...state,
    serviceInfos: state.serviceInfos.map((s) => {
      return {
        ...s,
        selected: getSelected(s, payload.serviceInfo, payload.selected)
    }})
  }
}

const reduceNewReservation = (
  state: INewReservationState = initialNewReservationState,
  action: NewReservationActionTypes
): INewReservationState => {
  switch (action.type) {
    case BusStopsStepTypeKeys.FETCHING_SERVICES:
      return { ...state, isFetchingServices: true }
    case BusStopsStepTypeKeys.FETCH_SERVICES_SUCCESSFUL:
      return {
        ...updateServices(state, Array.isArray(action.payload) ? [...action.payload] : [action.payload]),
        isFetchingServices: false
      }
    case BusStopsStepTypeKeys.SET_IS_SEARCHING: {
      return { ...state, isSearching: action.payload }
    }
    case BusStopsStepTypeKeys.SEARCH_STOPS: {
      return { ...state, nameStopSearched: action.payload }
    }
    case BusStopsStepTypeKeys.SET_SEARCH_STOPS: {
      return { ...state, stopsSearched: action.payload }
    }
    case BusStopsStepTypeKeys.SET_SEARCHED_RESULT: {
      return {
        ...state,
        searchedType: undefined,
        nameStopSearched: action.payload.name,
        coordinates: undefined
      }
    }
    case BusStopsStepTypeKeys.SET_BUS_STOP:
      return {
        ...updateBusStops(state, action.payload.stop),
        isMarkerSelected: false
      }
    case BusStopsStepTypeKeys.FETCHING_DESTINATION_BUS_STOPS:
      return {
        ...state,
        isFetchingDestinationBusStops: true
      }
    case BusStopsStepTypeKeys.FETCH_DESTINATION_BUS_STOPS_SUCCESSFUL:
      return {
        ...state,
        isFetchingDestinationBusStops: false,
        destinationBusStops: action.payload
      }
    case DateStepTypeKeys.SET_TYPE_SPECIFIC_DATE_FLAG:
      return {
        ...state,
        specificDate: action.payload
      }
    case DateStepTypeKeys.SET_PICKED_DATE:
      return {
        ...state,
        pickedDate: action.payload
      }
    case DateStepTypeKeys.SET_PICKED_SEATS:
      return {
        ...state,
        pickedSeats: action.payload.seats,
        pickedPrmSeats: action.payload.prmSeats
      }
    case RouteOptionStepTypeKeys.FETCHING_ROUTE_OPTIONS:
      return {
        ...state,
        isFetchingRouteOptions: true
      }
    case RouteOptionStepTypeKeys.FETCHING_ROUTE_OPTIONS_ECORRIDOR:
      return {
        ...state,
        isFetchingRouteOptions: true
      }
    case RouteOptionStepTypeKeys.FETCH_ROUTE_OPTIONS_SUCCESSFUL:
      if (state.pickedAvailability) {
        const updatedAvailability = action.payload.availabilities && action.payload.availabilities.find((availability: any) => availability.id === state.pickedAvailability?.id)
        if (updatedAvailability) state.pickedAvailability = updatedAvailability
        else state.pickedAvailability = undefined
      }
      return {
        ...state,
        isFetchingRouteOptions: false,
        routeOptionsAndFailures: action.payload
      }
    case RouteOptionStepTypeKeys.FETCH_ROUTE_OPTIONS_SUCCESSFUL_ECORRIDOR:
      return {
        ...state,
        isFetchingRouteOptions: false,
        routeOptions: action.payload
      }
    case RouteOptionStepTypeKeys.SET_AVAILABILITY:
      return {
        ...state,
        pickedAvailability: action.payload
      }
    case BusStopsStepTypeKeys.RESET_NEW_RESERVATION_FIELDS:
      // An edit may read its service differently (see editableServiceInfo): it is fetched again.
      const serviceInfos = state.isEdit ? [] : state.serviceInfos
      return {
        ...initialNewReservationState,
        serviceInfos,
        isWheelPicker: isWheelPicker(serviceInfos),
        showTripTypeOptions: false
      }
    case BusStopsStepTypeKeys.SET_NEW_RESERVATION_FIELDS:
      return reservationToReservationState(state, action.payload)
    case BusStopsStepTypeKeys.SET_FAVORITE_NEW_RESERVATION_FIELDS:
      return favoriteToReservationState(state, action.payload)
    case ConfirmStepTypeKeys.CONFIRMING_NEW_RESERVATION:
      return {
        ...state,
        isConfirmingReservation: true
      }
    case ConfirmStepTypeKeys.CONFIRM_NEW_RESERVATION:
      return {
        ...state,
        isConfirmingReservation: true,
        paymentState: IPaymentState.DOING_PAYMENT,
        paymentAction: action.payload.payOption,
        activePassId: action.payload.activePassId
      }
    case ConfirmStepTypeKeys.CONFIRMING_NEW_RESERVATION:
      return {
        ...state,
        isConfirmingReservation: true,
        paymentState: IPaymentState.DOING_PAYMENT
      }
    case ConfirmStepTypeKeys.CONFIRM_NEW_RESERVATION_SUCCESSFUL:
      return {
        ...state,
        isConfirmingReservation: false,
        userCreated: action.payload
      }
    case ConfirmStepTypeKeys.CONFIRM_NEW_RESERVATION_FAILED:
      return {
        ...state,
        isConfirmingReservation: false,
        paymentState: IPaymentState.PAYMENT_FAILED
      }
    case BusStopsStepTypeKeys.SET_SELECT_MARKER_FLAG:
      return { ...state, isMarkerSelected: action.payload }
    case BusStopsStepTypeKeys.SET_MAP_CHANGED_CENTER:
      return {
        ...state,
        mapChangedCenter: action.payload
      }
    case BusStopsStepTypeKeys.RESET_MAP_CHANGED_CENTER:
      return {
        ...state,
        mapChangedCenter: null
      }
    case BusStopsStepTypeKeys.SET_SERVICE_SELECTED:
      return setServices(state, action.payload)
    case BusStopsStepTypeKeys.SET_USER_LOCATION:
      return {
        ...state,
        userLocation: action.payload,
      }
    case BusStopsStepTypeKeys.SET_ROUND_TRIP:
      return {
        ...state,
        isRoundTrip: action.payload
      }
    case BusStopsStepTypeKeys.SET_SHOW_TRIP_TYPE_OPTIONS:
      return {
        ...state,
        showTripTypeOptions: action.payload
      }

    case BusStopsStepTypeKeys.SET_SINGLE_BUS_STOP_DIRECTION:
      let originStop = action.payload.isOrigin ? state.singleStopSelected : undefined
      let destinationStop = action.payload.isOrigin ? undefined : state.singleStopSelected

      if (action.payload.stop) {
        if (action.payload.isOrigin) originStop = action.payload.stop
        else destinationStop = action.payload.stop
      }
      return {
        ...state,
        pickedOriginBusStop: originStop,
        pickedDestinationBusStop: destinationStop,
        // An edit stays on the stops step with the stop picked, so it has to remain the selected one.
        singleStopSelected: state.isEdit && action.payload.stop ? action.payload.stop : state.singleStopSelected,
        isOrigin: action.payload.isOrigin,
        isRoundTrip: checkIfIsRoundTripAndShowTripTypeOptions(state.serviceInfos, state.pickedOriginBusStop, state.pickedDestinationBusStop).isRoundTrip
      }
    case BusStopsStepTypeKeys.SHOW_SINGLE_STOP_MODAL:
      return {
        ...state,
        isSingleStopModalOpen: action.payload.isSingleStopModalOpen,
        pickedOriginBusStop: action.payload.isSingleStopModalOpen ? action.payload.stop : state.pickedOriginBusStop,
        singleStopSelected: action.payload.stop ?? state.singleStopSelected,
        isRoundTrip: action.payload.stop?.serviceInfo?.roundTrips ?? state.isRoundTrip,
        showTripTypeOptions: action.payload.stop ? checkIfIsRoundTripAndShowTripTypeOptions(state.serviceInfos, action.payload.stop)?.showTripTypeOptions : state.showTripTypeOptions
      }
    case BusStopsStepTypeKeys.RESET_SINGLE_BUS_STOP:
      return {
        ...state,
        pickedOriginBusStop: undefined,
        pickedDestinationBusStop: undefined,
        pickedAvailability: undefined,
        returnPickedAvailability: undefined,
        pickedDate: new Date(),
        returnPickedDate: new Date(),
        weekDaysListReturn: [],
        singleStopSelected: undefined,
        isOrigin: true,
        showTripTypeOptions: false,
        previewOriginStop: undefined,
        previewDestinationStop: undefined
      }
    case BusStopsStepTypeKeys.SET_IS_SEARCHING_COORDS_IN_MAP:
      return {
        ...state,
        isSearchingCoordsInMap: action.payload
      }
    case BusStopsStepTypeKeys.SET_IS_MAP_CENTER_WITHIN_OUTLINES:
      return {
        ...state,
        isMapCenterWithinOutlines: action.payload
      }
    case BusStopsStepTypeKeys.SET_PREVIEW_ORIGIN_STOP:
      return {
        ...state,
        previewOriginStop: action.payload
      }
    case BusStopsStepTypeKeys.SET_PREVIEW_DESTINATION_STOP:
      return {
        ...state,
        previewDestinationStop: action.payload
      }
    case BusStopsStepTypeKeys.SET_CACHED_ADDRESSES:
      return {
        ...state,
        cachedAddresses: action.payload
      }
    case BusStopsStepTypeKeys.ADD_CACHED_ADDRESS:
      return {
        ...state,
        // Most recent first, without repeating the same address.
        cachedAddresses: [
          action.payload,
          ...state.cachedAddresses.filter(address => address.id !== action.payload.id)
        ].slice(0, CACHED_ADDRESSES_LIMIT)
      }
    case BusStopsStepTypeKeys.SET_IS_FETCHING_STOP_AVAILABILITY_TIMES:
      return {
        ...state,
        isFetchingStopAvailabilityTimes: action.payload
      }
    case BusStopsStepTypeKeys.SET_STOP_AVAILABILITY_TIMES:
      return {
        ...state,
        stopAvailabilityTimes: action.payload,
        isFetchingStopAvailabilityTimes: false
      }
    case BusStopsStepTypeKeys.RESET_STOP_AVAILABILITY_TIMES:
      return {
        ...state,
        stopAvailabilityTimes: [],
        isFetchingStopAvailabilityTimes: false
      }
    case DateStepTypeKeys.SET_RETURN_TYPE_SPECIFIC_DATE_FLAG:
      return {
        ...state,
        returnSpecificDate: action.payload
      }
    case DateStepTypeKeys.SET_RETURN_PICKED_DATE:
      return {
        ...state,
        returnPickedDate: action.payload
      }
    case DateStepTypeKeys.SET_WEEK_DAYS_LIST_RETURN:
      return {
        ...state,
        weekDaysListReturn: action.payload
      }
    case RouteOptionStepTypeKeys.FETCHING_RETURN_ROUTE_OPTIONS:
      return {
        ...state,
        isFetchingReturnRouteOptions: true
      }
    case RouteOptionStepTypeKeys.FETCH_RETURN_ROUTE_OPTIONS_SUCCESSFUL:
      if (state.returnPickedAvailability) {
        const updatedAvailability = action.payload.availabilities.find((availability: any) => availability.id === state.returnPickedAvailability?.id)
        if (updatedAvailability) state.returnPickedAvailability = updatedAvailability
        else state.returnPickedAvailability = undefined
      }
      return {
        ...state,
        isFetchingReturnRouteOptions: false,
        returnRouteOptionsAndFailures: action.payload
      }
    case RouteOptionStepTypeKeys.SET_RETURN_AVAILABILITY:
      return {
        ...state,
        returnPickedAvailability: action.payload
      }
    default:
      return state
  }
}

// An edit updates what was booked: a round trip stays a round trip and a single trip stays single,
// whatever the stops picked on the way say.
const newReservation = (
  state: INewReservationState = initialNewReservationState,
  action: NewReservationActionTypes
): INewReservationState => {
  const nextState = reduceNewReservation(state, action)
  if (!nextState.isEdit) return nextState

  const isRoundTrip = !!nextState.pickedRelatedReservationId
  return nextState.isRoundTrip === isRoundTrip ? nextState : { ...nextState, isRoundTrip }
}

export default newReservation
